import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { EmployeeSchedule, ScheduleDay } from '@/types/schedule.types';

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

interface ScheduleGridProps {
  employees: EmployeeSchedule[];
  dates: Date[];
  onCellClick?: (employee: EmployeeSchedule, day: ScheduleDay) => void;
  onEmployeeClick?: (employee: EmployeeSchedule) => void;
  selectedEmployeeId?: string | null;
}

const getDayColorClass = (
  isWorking: boolean,
  scheduleType: string,
  source: string,
): string => {
  // Exception has special styling
  if (source === 'schedule_exception') {
    if (isWorking) {
      return 'bg-amber-200 hover:bg-amber-300 border-amber-400';
    }
    return 'bg-amber-100 hover:bg-amber-200 border-amber-300';
  }

  // Normal schedule
  if (!isWorking || scheduleType === 'day_off') {
    return 'bg-gray-100 hover:bg-gray-200 border-gray-200';
  }

  switch (scheduleType) {
    case 'working':
      return 'bg-green-100 hover:bg-green-200 border-green-200';
    case 'night_shift':
      return 'bg-indigo-100 hover:bg-indigo-200 border-indigo-200';
    case 'holiday':
      return 'bg-red-100 hover:bg-red-200 border-red-200';
    case 'reduced':
      return 'bg-yellow-100 hover:bg-yellow-200 border-yellow-200';
    default:
      return 'bg-gray-50 hover:bg-gray-100 border-gray-100';
  }
};

const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  const value = time.toString();
  const timePart = value.includes('T') ? value.split('T')[1] : value;
  return timePart.slice(0, 5); // HH:mm
};

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// Пояснения к итоговым колонкам (заголовки и итоговая строка)
const STAT_COLUMN_HINTS = {
  staffing:
    'Штатное расписание: сколько смен положено сотруднику за период по штатному графику из 1С.',
  working:
    'Запланированные рабочие смены за период по графику в системе, с учётом исключений и замен.',
  actual:
    'Фактически отработанные смены за прошедшие дни периода — по отметкам прихода на момент формирования таблицы. Сегодняшний день не учитывается.',
  daysOff: 'Выходные дни за период по графику.',
} as const;

const TOTALS_HINT_SUFFIX = ' Сумма по всем сотрудникам отдела.';

const HintTooltip: React.FC<{ text: string; children: React.ReactNode }> = ({
  text,
  children,
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px]">
        <p className="text-xs">{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);


export const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  employees,
  dates,
  onCellClick,
  onEmployeeClick,
  selectedEmployeeId,
}) => {
  // Find schedule day for employee and date
  const findScheduleDay = (
    employee: EmployeeSchedule,
    date: Date,
  ): ScheduleDay | undefined => {
    // Use local date formatting to avoid timezone issues
    // toISOString() converts to UTC which can shift the date by -1 day
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return employee.schedule.find((s) => s.date === dateStr);
  };

  // Compute daily totals: day shifts vs night shifts
  const dailyShiftCounts = useMemo(() => {
    const dayCounts = new Map<string, number>();
    const nightCounts = new Map<string, number>();
    for (const date of dates) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      let dayCount = 0;
      let nightCount = 0;
      for (const emp of employees) {
        const scheduleDay = emp.schedule.find((s) => s.date === dateStr);
        if (scheduleDay?.is_working_day) {
          if (scheduleDay.schedule_type === 'night_shift') {
            nightCount++;
          } else {
            dayCount++;
          }
        }
      }
      dayCounts.set(dateStr, dayCount);
      nightCounts.set(dateStr, nightCount);
    }
    return { dayCounts, nightCounts };
  }, [employees, dates]);

  // Grand totals for the statistics columns (ШР / рабочих / факт / выходных)
  const columnTotals = useMemo(() => {
    let staffingShifts = 0;
    let workingDays = 0;
    let actualShifts = 0;
    let daysOff = 0;
    for (const emp of employees) {
      staffingShifts += emp.staffing_shifts ?? 0;
      workingDays += emp.total_working_days;
      actualShifts += emp.actual_shifts ?? 0;
      daysOff += emp.total_days_off;
    }
    return { staffingShifts, workingDays, actualShifts, daysOff };
  }, [employees]);

  return (
    <div className="border rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
      <div className="overflow-auto flex-1">
        <table className="w-full border-collapse">
          {/* Header with dates */}
          <thead className="sticky top-0 z-30">
            <tr className="bg-muted">
              {/* Employee column header */}
              <th className="sticky left-0 z-40 bg-muted border-r min-w-[200px] p-2 text-left font-medium">
                Сотрудник
              </th>
              {/* Date headers */}
              {dates.map((date) => {
                const dayOfWeek = date.getDay();
                const dayNum = date.getDate();
                const isTodayDate = isToday(date);
                const isWeekendDay = isWeekend(date);

                return (
                  <th
                    key={date.toISOString()}
                    className={cn(
                      'min-w-[50px] p-1 text-center text-xs font-medium border-l',
                      isTodayDate && 'bg-blue-100 border-blue-300',
                      isWeekendDay && !isTodayDate && 'bg-gray-100',
                    )}
                  >
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          'text-[10px]',
                          isWeekendDay ? 'text-red-500' : 'text-muted-foreground',
                        )}
                      >
                        {WEEKDAY_NAMES[dayOfWeek]}
                      </span>
                      <span
                        className={cn(
                          'font-bold',
                          isTodayDate && 'text-blue-600',
                          isWeekendDay && !isTodayDate && 'text-red-500',
                        )}
                      >
                        {dayNum}
                      </span>
                    </div>
                  </th>
                );
              })}
              {/* Statistics columns */}
              <th className="sticky right-[150px] z-30 min-w-[50px] p-1 text-center text-xs font-medium border-l bg-blue-50">
                <HintTooltip text={STAT_COLUMN_HINTS.staffing}>
                  <span className="cursor-help">ШР</span>
                </HintTooltip>
              </th>
              <th className="sticky right-[100px] z-30 min-w-[50px] p-1 text-center text-xs font-medium border-l bg-green-50">
                <HintTooltip text={STAT_COLUMN_HINTS.working}>
                  <span className="cursor-help">Раб.</span>
                </HintTooltip>
              </th>
              <th className="sticky right-[50px] z-30 min-w-[50px] p-1 text-center text-xs font-medium border-l bg-emerald-50">
                <HintTooltip text={STAT_COLUMN_HINTS.actual}>
                  <span className="cursor-help">Факт</span>
                </HintTooltip>
              </th>
              <th className="sticky right-0 z-30 min-w-[50px] p-1 text-center text-xs font-medium border-l bg-gray-100">
                <HintTooltip text={STAT_COLUMN_HINTS.daysOff}>
                  <span className="cursor-help">Вых.</span>
                </HintTooltip>
              </th>
            </tr>
          </thead>
          {/* Body with employees */}
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td
                  colSpan={dates.length + 5}
                  className="p-8 text-center text-muted-foreground"
                >
                  Нет сотрудников в отделе
                </td>
              </tr>
            ) : (
              employees.map((employee) => {
                const isSelected = selectedEmployeeId === employee.employee_id;
                return (
                <tr key={employee.employee_id} className={cn('hover:bg-muted/50', isSelected && 'bg-blue-50')}>
                  {/* Employee name */}
                  <td className={cn('sticky left-0 z-10 border-r p-2 min-w-[200px]', isSelected ? 'bg-blue-50' : 'bg-background')}>
                    <button
                      type="button"
                      onClick={() => onEmployeeClick?.(employee)}
                      className="flex flex-col text-left hover:underline cursor-pointer"
                    >
                      <span className={cn('font-medium text-sm', isSelected && 'text-blue-700')}>
                        {employee.last_name} {employee.first_name}
                      </span>
                      {employee.position && (
                        <span className="text-xs text-muted-foreground">
                          {employee.position.name}
                        </span>
                      )}
                    </button>
                  </td>
                  {/* Schedule cells */}
                  {dates.map((date) => {
                    const scheduleDay = findScheduleDay(employee, date);
                    const isTodayDate = isToday(date);

                    if (!scheduleDay) {
                      return (
                        <td
                          key={date.toISOString()}
                          className={cn(
                            'p-0 border-l',
                            isTodayDate && 'ring-2 ring-blue-400 ring-inset',
                          )}
                        >
                          <div className="h-10 bg-gray-50 flex items-center justify-center">
                            <span className="text-[10px] text-gray-400">-</span>
                          </div>
                        </td>
                      );
                    }

                    const colorClass = getDayColorClass(
                      scheduleDay.is_working_day,
                      scheduleDay.schedule_type,
                      scheduleDay.source,
                    );

                    const timeDisplay = scheduleDay.is_working_day
                      ? `${formatTime(scheduleDay.shift_start)} - ${formatTime(scheduleDay.shift_end)}`
                      : 'Вых';

                    const tooltipContent = (
                      <div className="text-xs space-y-1">
                        <div className="font-medium">
                          {scheduleDay.schedule_type === 'night_shift'
                            ? 'Ночная смена'
                            : scheduleDay.is_working_day
                              ? 'Рабочий день'
                              : 'Выходной'}
                        </div>
                        {scheduleDay.is_working_day && (
                          <div>
                            {formatTime(scheduleDay.shift_start)} -{' '}
                            {formatTime(scheduleDay.shift_end)}
                          </div>
                        )}
                        <div className="text-muted-foreground">
                          Источник:{' '}
                          {scheduleDay.source === 'schedule_exception'
                            ? 'Исключение'
                            : scheduleDay.source === 'template_calendar'
                              ? 'Календарь шаблона'
                              : scheduleDay.source === 'work_schedule'
                                ? 'Персональный график'
                                : 'Паттерн шаблона'}
                        </div>
                        {scheduleDay.exception && (
                          <>
                            {scheduleDay.exception.reason && (
                              <div>Причина: {scheduleDay.exception.reason}</div>
                            )}
                            {scheduleDay.exception.is_replacement && (
                              <div className="text-amber-600">
                                Замена: {scheduleDay.exception.replacement_employee_name}
                              </div>
                            )}
                          </>
                        )}
                        {scheduleDay.is_overtime && (
                          <div className="text-purple-700 font-medium">
                            Сверхурочные
                          </div>
                        )}
                      </div>
                    );

                    return (
                      <td
                        key={date.toISOString()}
                        className={cn(
                          'p-0 border-l',
                          isTodayDate && 'ring-2 ring-blue-400 ring-inset',
                        )}
                      >
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => onCellClick?.(employee, scheduleDay)}
                                className={cn(
                                  'relative h-10 w-full flex flex-col items-center justify-center border cursor-pointer transition-colors',
                                  colorClass,
                                  scheduleDay.source === 'schedule_exception' &&
                                    'border-2 border-dashed',
                                  scheduleDay.is_overtime &&
                                    'ring-2 ring-purple-500 ring-inset',
                                )}
                              >
                                {scheduleDay.is_working_day ? (
                                  <>
                                    <span className="text-[10px] font-medium leading-tight">
                                      {formatTime(scheduleDay.shift_start)}
                                    </span>
                                    {scheduleDay.schedule_type === 'night_shift' && (
                                      <span className="text-[8px] text-indigo-600 font-medium">ноч</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-[10px] text-gray-500">вых</span>
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px]">
                              {tooltipContent}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    );
                  })}
                  {/* Statistics cells */}
                  <td
                    className="sticky right-[150px] z-10 p-1 text-center text-sm font-medium border-l bg-blue-50"
                    title="Смены по штатному графику из 1С за период"
                  >
                    {employee.staffing_shifts ?? '—'}
                  </td>
                  <td className="sticky right-[100px] z-10 p-1 text-center text-sm font-medium border-l bg-green-50">
                    {employee.total_working_days}
                  </td>
                  <td
                    className="sticky right-[50px] z-10 p-1 text-center text-sm font-medium border-l bg-emerald-50"
                    title="Фактически отработанные смены за прошедшие дни периода (по отметкам)"
                  >
                    {employee.actual_shifts ?? 0}
                  </td>
                  <td className="sticky right-0 z-10 p-1 text-center text-sm font-medium border-l bg-gray-100">
                    {employee.total_days_off}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
          {/* Summary rows: day shifts and night shifts per day */}
          {employees.length > 0 && (
            <tfoot className="sticky bottom-0 z-20">
              {/* Day shifts row */}
              <tr className="bg-muted/80 border-t-2 border-border">
                <td className="sticky left-0 z-40 bg-muted border-r p-2 min-w-[200px] text-left">
                  <span className="font-medium text-sm">На смене</span>
                </td>
                {dates.map((date) => {
                  const year = date.getFullYear();
                  const m = String(date.getMonth() + 1).padStart(2, '0');
                  const d = String(date.getDate()).padStart(2, '0');
                  const dateStr = `${year}-${m}-${d}`;
                  const count = dailyShiftCounts.dayCounts.get(dateStr) ?? 0;
                  const isTodayDate = isToday(date);

                  return (
                    <td
                      key={date.toISOString()}
                      className={cn(
                        'p-0 border-l text-center',
                        isTodayDate && 'bg-blue-50',
                      )}
                    >
                      <div className="h-8 flex items-center justify-center">
                        <span
                          className={cn(
                            'text-xs font-bold',
                            count > 0 ? 'text-green-700' : 'text-gray-400',
                          )}
                        >
                          {count}
                        </span>
                      </div>
                    </td>
                  );
                })}
                <td className="sticky right-[150px] z-10 p-1 text-center border-l bg-blue-100">
                  <HintTooltip
                    text={STAT_COLUMN_HINTS.staffing + TOTALS_HINT_SUFFIX}
                  >
                    <span className="text-sm font-bold text-blue-800 cursor-help">
                      {columnTotals.staffingShifts}
                    </span>
                  </HintTooltip>
                </td>
                <td className="sticky right-[100px] z-10 p-1 text-center border-l bg-green-100">
                  <HintTooltip
                    text={STAT_COLUMN_HINTS.working + TOTALS_HINT_SUFFIX}
                  >
                    <span className="text-sm font-bold text-green-800 cursor-help">
                      {columnTotals.workingDays}
                    </span>
                  </HintTooltip>
                </td>
                <td className="sticky right-[50px] z-10 p-1 text-center border-l bg-emerald-100">
                  <HintTooltip
                    text={STAT_COLUMN_HINTS.actual + TOTALS_HINT_SUFFIX}
                  >
                    <span className="text-sm font-bold text-emerald-800 cursor-help">
                      {columnTotals.actualShifts}
                    </span>
                  </HintTooltip>
                </td>
                <td className="sticky right-0 z-10 p-1 text-center border-l bg-gray-200">
                  <HintTooltip
                    text={STAT_COLUMN_HINTS.daysOff + TOTALS_HINT_SUFFIX}
                  >
                    <span className="text-sm font-bold text-gray-700 cursor-help">
                      {columnTotals.daysOff}
                    </span>
                  </HintTooltip>
                </td>
              </tr>
              {/* Night shifts row */}
              <tr className="bg-indigo-50/60 border-t border-border">
                <td className="sticky left-0 z-40 bg-indigo-50 border-r p-2 min-w-[200px] text-left">
                  <span className="font-medium text-sm text-indigo-700">На смене ночь</span>
                </td>
                {dates.map((date) => {
                  const year = date.getFullYear();
                  const m = String(date.getMonth() + 1).padStart(2, '0');
                  const d = String(date.getDate()).padStart(2, '0');
                  const dateStr = `${year}-${m}-${d}`;
                  const count = dailyShiftCounts.nightCounts.get(dateStr) ?? 0;
                  const isTodayDate = isToday(date);

                  return (
                    <td
                      key={date.toISOString()}
                      className={cn(
                        'p-0 border-l text-center',
                        isTodayDate && 'bg-blue-50',
                      )}
                    >
                      <div className="h-8 flex items-center justify-center">
                        <span
                          className={cn(
                            'text-xs font-bold',
                            count > 0 ? 'text-indigo-700' : 'text-gray-400',
                          )}
                        >
                          {count}
                        </span>
                      </div>
                    </td>
                  );
                })}
                <td className="sticky right-[150px] z-10 p-1 text-center border-l bg-blue-50" />
                <td className="sticky right-[100px] z-10 p-1 text-center border-l bg-green-50" />
                <td className="sticky right-[50px] z-10 p-1 text-center border-l bg-emerald-50" />
                <td className="sticky right-0 z-10 p-1 text-center border-l bg-gray-100" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* Legend */}
      <div className="border-t p-3 flex flex-wrap gap-4 text-xs bg-muted/30">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
          <span className="text-muted-foreground">Рабочий день</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-indigo-100 border border-indigo-200" />
          <span className="text-muted-foreground">Ночная смена</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
          <span className="text-muted-foreground">Выходной</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-100 border border-red-200" />
          <span className="text-muted-foreground">Праздник</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200" />
          <span className="text-muted-foreground">Сокращённый</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-amber-200 border-2 border-dashed border-amber-400" />
          <span className="text-muted-foreground">Исключение</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-white ring-2 ring-purple-500 ring-inset" />
          <span className="text-muted-foreground">Сверхурочные</span>
        </div>
      </div>
    </div>
  );
};

export default ScheduleGrid;
